export const locales = [
  { code: "en", label: "English", dir: "ltr" },
  { code: "ko", label: "한국어", dir: "ltr" },
  { code: "ja", label: "日本語", dir: "ltr" },
  { code: "zh", label: "中文", dir: "ltr" },
  { code: "hi", label: "हिन्दी", dir: "ltr" },
  { code: "de", label: "Deutsch", dir: "ltr" },
  { code: "fr", label: "Français", dir: "ltr" },
  { code: "es", label: "Español", dir: "ltr" },
  { code: "pt", label: "Português", dir: "ltr" },
  { code: "ar", label: "العربية", dir: "rtl" },
] as const;

export type Locale = (typeof locales)[number]["code"];

type Dictionary = Record<string, string>;

const en: Dictionary = {
  product: "Sovasta Companion",
  overview: "Overview",
  hatchery: "Hatchery",
  growth: "Growth",
  skills: "Skills",
  pricing: "Pricing",
  support: "Support",
  account: "Account",
  install: "Install app",
  signin: "Sign in",
  heroKicker: "PERSONAL AI COMPANION · GROWTH LOOP",
  heroTitle: "Hatch an AI companion that knows your day and grows with you.",
  heroBody:
    "Sovasta Companion turns one personal agent into a living companion: name it, train it, unlock skills, and let it coordinate the Sovasta ecosystem with your permission.",
  heroPrimary: "Hatch my companion",
  heroSecondary: "See growth loop",
  socialProof: "18 Sovasta surfaces · 5 core stats · One Sovasta ID",
  demoTitle: "Today’s companion",
  demoName: "Mira · Lv. 7",
  demoMood: "Mood: curious",
  demoLine1: "07:00 prepared your morning audio queue.",
  demoLine2: "12:00 suggested a lighter lunch with Sovasta Cook.",
  demoLine3: "21:00 will ask Sovasta Music for a sleep routine.",
  onboardingTitle: "Four short scenes before you start",
  onboardingSubtitle: "Text-first onboarding disappears after the preview or when you close it.",
  scene1: "1. Receive the egg",
  scene1Body: "Start with a private companion egg. It has no memory until you choose what to share.",
  scene2: "2. Name and shape it",
  scene2Body: "Pick a name, tone, boundaries, and the kind of help you want every day.",
  scene3: "3. Unlock useful skills",
  scene3Body: "Focus, care, social, trust, and money skills unlock as you use Sovasta services.",
  scene4: "4. Let it return with evidence",
  scene4Body: "Every action comes back as a clear timeline you can approve, pause, or delete.",
  close: "Close",
  hatchTitle: "Create the first egg without signing in.",
  hatchBody: "Guest mode stores the draft on this device. Sign in only when you save, sync, or activate cross-site memory.",
  companionName: "Companion name",
  purpose: "Main purpose",
  boundary: "Boundary",
  generate: "Generate local companion card",
  save: "Save with One Sovasta ID",
  draftCard: "Local draft card",
  growthTitle: "Grow through daily loops.",
  growthBody: "The companion improves when it completes useful routines and you rate whether the help was actually valuable.",
  statInt: "Intelligence",
  statEmp: "Empathy",
  statMem: "Memory",
  statAut: "Autonomy",
  statTrust: "Trust",
  level: "Level",
  streak: "Day streak",
  nextUnlock: "Next unlock",
  skillsTitle: "Permission-based skill tree.",
  skillsBody: "Skills stay off until you grant a clear scope. Your companion never acts silently outside the boundary you set.",
  skillCare: "Care",
  skillCareBody: "Wellness check-ins, music routines, spoken daily briefings.",
  skillWork: "Work",
  skillWorkBody: "Research, blog drafts, code summaries, calendar preparation.",
  skillSocial: "Social",
  skillSocialBody: "Friendship prompts, relationship memory, social posting drafts.",
  skillTrust: "Trust",
  skillTrustBody: "Identity, permissions, evidence logs, agent risk scoring.",
  pricingTitle: "Pricing that follows trust, memory, and autonomy.",
  pricingBody: "Start free. Pay only when your companion needs synced memory, cross-site actions, or family/team controls.",
  planFree: "Egg",
  planGrow: "Grow",
  planFamily: "Family",
  planStudio: "Studio",
  choose: "Choose plan",
  freeFeature: "One local companion draft",
  growFeature: "Synced memory and 10 active skills",
  familyFeature: "Shared household companion rules",
  studioFeature: "Creator and team companion operations",
  supportTitle: "A clear next step whenever you need one.",
  supportBody: "Ask the AI guide for product help or contact a person when account, billing, or privacy context matters.",
  askGuide: "Ask the AI guide",
  contactPerson: "Contact a person",
  message: "Message",
  send: "Send",
  subject: "Subject",
  faqTitle: "Frequently asked questions",
  faq1: "Is my companion a separate account?",
  faq1Body: "No. It is connected to One Sovasta ID so you keep one identity and one permission model.",
  faq2: "Can it act across other Sovasta sites?",
  faq2Body: "Yes, but only after you explicitly unlock each skill and boundary.",
  faq3: "Can I delete memory?",
  faq3Body: "Yes. Memory is shown as evidence cards and can be paused or deleted.",
  accountTitle: "Guest mode is active.",
  accountBody: "Your draft stays in this browser. Sign in to sync memory, install skills, and manage Stripe billing.",
  terms: "Terms",
  privacy: "Privacy",
  footer: "Personal companion for daily routines, emotional continuity, and permissioned Sovasta ecosystem actions.",
  community: "Community",
  signout: "Sign out",
  signinModalTitle: "Sign in",
  signinModalBody: "Sign in with One Sovasta ID to save your companion, sync memory, and post in Community. Not on this device yet? Install Sovasta Companion first, then sign in.",
  signinModalContinue: "Sign in",
  communityEyebrow: "CUSTOMER VOICE",
  communityTitle: "Community",
  communityBody: "Tell us what your companion still needs, see what other members are asking for, and find Sovasta Companion discussed in the wider community.",
  communityWallTitle: "The member wall",
  communityWallGate: "Sign in to read and post on the member wall. Every post is tied to a real Sovasta ID — nothing here is seeded.",
  communityShareLabel: "What should your companion do next?",
  communityTopicLabel: "Topic",
  communityTitleLabel: "Short summary",
  communityPost: "Post to the member wall",
  communityNeedMore: "Say a bit more before posting.",
  communityPostSent: "Posted to the member wall.",
  communityPostError: "Couldn't reach the member wall right now.",
  communityLoadError: "Couldn't load the member wall right now.",
  communityEmpty: "No member posts yet — be the first to share what you need.",
  communityReplies: "replies",
  communityBack: "Back to Community",
  communityNoReplies: "No replies yet.",
  communityYourReply: "Your reply",
  communityPostReply: "Post reply",
  catGeneral: "General",
  catSkills: "Skills & permissions",
  catGrowth: "Growth loop",
  catBilling: "Billing & pricing",
  externalTitle: "Discuss in the open",
  externalText: "Real, live search results — Sovasta Companion doesn't embed Reddit or Quora because neither platform allows third-party embedding.",
  startRedditPost: "Start a Reddit post",
  joinSubreddit: "Browse r/artificial",
  exploreQuora: "Explore AI companions on Quora",
  externalDisclosure: "Reddit and Quora are independent third-party services Sovasta Companion doesn't moderate or control.",
  consentTitle: "Before you post publicly",
  consentText: "Your post and replies are visible to every other signed-in Sovasta Companion member, alongside the email on your account.",
  consentCheckbox: "I understand my post is visible to other members and agree to the Community rules in the Terms.",
  consentAgree: "Agree and post",
  guideHeroEyebrow: "FEATURE GUIDE",
  guideHeroTitle: "See everything Sovasta Companion can do for you",
  guideHeroCta: "Show me →",
  guideHeroDone: "You've explored it all — here's a refresher",
};

const dictionaries: Record<Locale, Dictionary> = {
  en,
  ko: {
    ...en,
    product: "Sovasta Companion",
    overview: "개요",
    hatchery: "부화",
    growth: "성장",
    skills: "스킬",
    pricing: "가격",
    support: "지원",
    account: "계정",
    install: "앱 설치",
    signin: "로그인",
    heroKicker: "개인 AI 동반자 · 성장 루프",
    heroTitle: "나의 하루를 알고 함께 성장하는 AI 동반자를 부화시키세요.",
    heroBody: "Sovasta Companion은 개인 에이전트를 살아 있는 동반자로 바꿉니다. 이름을 붙이고, 훈련하고, 스킬을 열고, 허락한 범위에서 Sovasta 생태계를 조율하게 하세요.",
    heroPrimary: "내 동반자 만들기",
    heroSecondary: "성장 루프 보기",
    supportTitle: "필요할 때 바로 다음 행동을 알려드립니다.",
  },
  ja: {
    ...en,
    overview: "概要",
    hatchery: "誕生",
    growth: "成長",
    skills: "スキル",
    pricing: "料金",
    support: "サポート",
    account: "アカウント",
    install: "アプリをインストール",
    signin: "サインイン",
    heroTitle: "あなたの一日を理解し、一緒に育つAIコンパニオンを孵化させましょう。",
    heroBody: "名前を付け、訓練し、権限を与えた範囲でSovastaエコシステムを支援します。",
  },
  zh: {
    ...en,
    overview: "概览",
    hatchery: "孵化",
    growth: "成长",
    skills: "技能",
    pricing: "价格",
    support: "支持",
    account: "账户",
    install: "安装应用",
    signin: "登录",
    heroTitle: "孵化一个理解你的一天并与你共同成长的 AI 伙伴。",
    heroBody: "命名、训练、解锁技能，并在你授权的边界内协调 Sovasta 生态服务。",
  },
  hi: {
    ...en,
    overview: "सारांश",
    hatchery: "हैचरी",
    growth: "विकास",
    skills: "कौशल",
    pricing: "मूल्य",
    support: "सहायता",
    account: "खाता",
    install: "ऐप इंस्टॉल करें",
    signin: "साइन इन",
    heroTitle: "ऐसा AI साथी बनाइए जो आपका दिन समझे और आपके साथ बढ़े।",
    heroBody: "नाम दें, प्रशिक्षित करें, कौशल खोलें, और अनुमति के साथ Sovasta सेवाओं को समन्वित कराएँ।",
  },
  de: {
    ...en,
    overview: "Überblick",
    hatchery: "Brutstätte",
    growth: "Wachstum",
    skills: "Fähigkeiten",
    pricing: "Preise",
    support: "Support",
    account: "Konto",
    install: "App installieren",
    signin: "Anmelden",
    heroTitle: "Brüte einen KI-Begleiter aus, der deinen Tag kennt und mit dir wächst.",
    heroBody: "Benenne, trainiere und aktiviere Fähigkeiten innerhalb klarer Berechtigungen im Sovasta-Ökosystem.",
  },
  fr: {
    ...en,
    overview: "Aperçu",
    hatchery: "Éclosion",
    growth: "Croissance",
    skills: "Compétences",
    pricing: "Tarifs",
    support: "Assistance",
    account: "Compte",
    install: "Installer l’app",
    signin: "Connexion",
    heroTitle: "Faites éclore un compagnon IA qui comprend votre journée et grandit avec vous.",
    heroBody: "Nommez-le, entraînez-le et activez ses compétences avec des permissions claires.",
  },
  es: {
    ...en,
    overview: "Resumen",
    hatchery: "Incubadora",
    growth: "Crecimiento",
    skills: "Habilidades",
    pricing: "Precios",
    support: "Soporte",
    account: "Cuenta",
    install: "Instalar app",
    signin: "Iniciar sesión",
    heroTitle: "Incuba un compañero de IA que entiende tu día y crece contigo.",
    heroBody: "Ponle nombre, entrénalo y activa habilidades con permisos claros en el ecosistema Sovasta.",
  },
  pt: {
    ...en,
    overview: "Visão geral",
    hatchery: "Incubação",
    growth: "Crescimento",
    skills: "Habilidades",
    pricing: "Preços",
    support: "Suporte",
    account: "Conta",
    install: "Instalar app",
    signin: "Entrar",
    heroTitle: "Crie um companheiro de IA que entende seu dia e cresce com você.",
    heroBody: "Dê um nome, treine e libere habilidades com permissões claras no ecossistema Sovasta.",
  },
  ar: {
    ...en,
    overview: "نظرة عامة",
    hatchery: "الفقس",
    growth: "النمو",
    skills: "المهارات",
    pricing: "الأسعار",
    support: "الدعم",
    account: "الحساب",
    install: "تثبيت التطبيق",
    signin: "تسجيل الدخول",
    heroTitle: "أنشئ رفيق ذكاء اصطناعي يفهم يومك وينمو معك.",
    heroBody: "امنحه اسماً ودرّبه وافتح مهاراته ضمن أذونات واضحة داخل منظومة Sovasta.",
  },
};

export function getDictionary(locale: Locale) {
  return dictionaries[locale] || dictionaries.en;
}

export function localeDir(locale: Locale) {
  return locales.find((item) => item.code === locale)?.dir || "ltr";
}
